/**
 * Нижняя панель вкладок и лист поверх боя по макету docs/ux/voxel/04-heroes.png — чистое представление
 * {@link TabsModel} + колбэки {@link TabsActions}. Раскладка «одна рука»: панель — у нижнего края (высота
 * `--kit-tabbar-h`, над ней HUD держит место), лист растёт вверх от панели, бой под ним не на паузе.
 *
 * Режим `panel` (десктоп, C6): те же вкладки и списки, но постоянной колонкой — полоса вкладок сверху, список под ней,
 * без кнопки «закрыть»; открыта всегда какая-то вкладка (по умолчанию Heroes), повторный тап её не закрывает.
 */
import type { HeroId, PurchaseRequest } from "@newfungame/contracts";
import { TABS, type TabId } from "../../app/store";
import { getBetaTabs } from "../../app/beta-slot";
import { Button, formatCompact, Icon, type IconName, Plate } from "../kit";
import type { Buy, HeroRow, LevelRow, StarRow, TabsModel, TechRow } from "./model";
import type { TabsT } from "./messages";
import { HintMark, type LevelHint } from "../hints";
import "./tabs.css";

export interface TabsActions {
  onSelect(tab: TabId): void;
  onClose(): void;
  onPurchase(req: PurchaseRequest): void;
}

export interface TabsViewProps {
  model: TabsModel;
  tab: TabId | null;
  actions: TabsActions;
  t: TabsT;
  /** `sheet` — нижняя панель и лист (по умолчанию); `panel` — постоянная колонка десктопа. */
  mode?: "sheet" | "panel";
  /** Подсказка новичку «прокачай»: на вкладке Level или на кнопке покупки в ней. */
  hint?: LevelHint;
}

const TAB_ICON: Record<TabId, IconName> = {
  heroes: "tab_heroes",
  level: "tab_level",
  stars: "tab_stars",
  tech: "tab_tech",
};

export const HERO_ICON: Record<HeroId, IconName> = {
  gunner: "gunner",
  bomber: "bomber",
  sparky: "sparky",
  blaze: "blaze",
  frosty: "frosty",
  // премиум-герои беты: портреты из их чиби-атласов (tools/ui-art/portraits.mjs)
  radiation: "hero_radiation",
  cryo: "hero_cryo",
  laser: "hero_laser",
  plague: "hero_plague",
};

function BuyButton({
  buy,
  req,
  testId,
  t,
  hinted = false,
  onPurchase,
}: {
  buy: Buy;
  req: PurchaseRequest;
  testId: string;
  t: TabsT;
  hinted?: boolean;
  onPurchase(req: PurchaseRequest): void;
}) {
  if (buy.ok) {
    const button = (
      <Button
        class={`tabs-buy${hinted ? " hint-target" : ""}`}
        icon="coin"
        data-testid={testId}
        onClick={() => onPurchase(req)}
      >
        {formatCompact(buy.cost)}
      </Button>
    );
    if (!hinted) return button;
    // кнопка обрезает содержимое — палец рядом с ней, в своей обёртке
    return (
      <span class="hint-anchor">
        {button}
        <HintMark label={t("tabs.hintBuy")} place="left" testId="hint-buy" />
      </span>
    );
  }
  const reason =
    buy.reason === "heroLocked"
      ? t("tabs.unlockWave", { n: buy.need })
      : buy.reason === "needLevel"
        ? t("tabs.needLevel", { n: buy.need })
        : buy.reason === "max"
          ? t("tabs.max")
          : buy.reason === "short"
            ? t("tabs.coinsShort")
            : t("tabs.needCoins", { n: formatCompact(buy.need) });
  return (
    <Button variant="dark" class="tabs-buy tabs-buy--off" disabled data-testid={testId} data-reason={buy.reason}>
      {(buy.reason === "coins" || buy.reason === "short") && buy.cost !== null && <Icon name="coin" size={18} />}
      <span class="tabs-buy__text">
        {(buy.reason === "coins" || buy.reason === "short") && buy.cost !== null && <b>{formatCompact(buy.cost)}</b>}
        <small>{reason}</small>
      </span>
    </Button>
  );
}

function Stars({ n, max = 5 }: { n: number; max?: number }) {
  return (
    <span class="tabs-stars" aria-hidden="true">
      {Array.from({ length: max }, (_, i) => (
        <span key={i} class={i < n ? "tabs-star tabs-star--on" : "tabs-star"}>
          ★
        </span>
      ))}
    </span>
  );
}

function HeroCard({ r, t }: { r: HeroRow; t: TabsT }) {
  return (
    <li
      class={`tabs-row${r.unlocked ? "" : " tabs-row--locked"}`}
      data-testid={`heroes-row-${r.id}`}
      data-unlocked={r.unlocked ? "1" : "0"}
    >
      <span class="tabs-ico">
        <Icon name={HERO_ICON[r.id]} size={40} />
      </span>
      <div class="tabs-main">
        <div class="tabs-title">
          {t(`hero.${r.id}.name`)}
          {r.unlocked ? <Stars n={r.stars} /> : null}
        </div>
        {r.unlocked ? (
          <>
            <div class="tabs-sub">
              {t("tabs.lv", { level: r.level, max: r.maxLevel })} · {t("tabs.power", { n: formatCompact(r.power) })}
            </div>
            <div class="tabs-skill">
              <b>{t(r.skillName)}</b> · {t("tabs.cooldown", { s: r.skillCooldown })}
              <br />
              {t(r.skillDescription)}
            </div>
          </>
        ) : (
          <div class="tabs-sub">{t("tabs.lockedAt", { n: r.unlockWave })}</div>
        )}
      </div>
    </li>
  );
}

function LevelCard({
  r,
  t,
  hinted,
  onPurchase,
}: {
  r: LevelRow;
  t: TabsT;
  hinted: boolean;
  onPurchase(req: PurchaseRequest): void;
}) {
  return (
    <li class="tabs-row" data-testid={`level-row-${r.id}`}>
      <span class="tabs-ico">
        <Icon name={HERO_ICON[r.id]} size={40} />
      </span>
      <div class="tabs-main">
        <div class="tabs-title">{t(`hero.${r.id}.name`)}</div>
        <div class="tabs-sub" data-testid={`level-lv-${r.id}`} data-level={r.level}>
          {t("tabs.lv", { level: r.level, max: r.maxLevel })}
        </div>
        <div class="tabs-sub">
          {r.nextPower === null
            ? t("tabs.damageMax", { n: formatCompact(r.power) })
            : t("tabs.damageNext", { from: formatCompact(r.power), to: formatCompact(r.nextPower) })}
        </div>
      </div>
      <BuyButton buy={r.buy} req={r.req} testId={`level-buy-${r.id}`} t={t} hinted={hinted} onPurchase={onPurchase} />
    </li>
  );
}

function StarCard({ r, t, onPurchase }: { r: StarRow; t: TabsT; onPurchase(req: PurchaseRequest): void }) {
  return (
    <li class="tabs-row" data-testid={`stars-row-${r.id}`}>
      <span class="tabs-ico">
        <Icon name={HERO_ICON[r.id]} size={40} />
      </span>
      <div class="tabs-main">
        <div class="tabs-title">
          {t(`hero.${r.id}.name`)}
          <Stars n={r.stars} />
        </div>
        <div class="tabs-sub">
          {r.next
            ? t(r.next.cooldownCut ? "tabs.starNextCd" : "tabs.starNext", { n: r.next.star, dmg: r.next.damagePct })
            : t("tabs.starsMax")}
        </div>
        <ol class="tabs-miles" aria-label={t("tabs.stars")}>
          {r.milestones.map((m) => (
            <li
              key={m.star}
              class={m.owned ? "tabs-mile tabs-mile--owned" : "tabs-mile"}
              data-star={m.star}
              data-level={m.level}
              title={t("tabs.starMilestone", { star: m.star, level: m.level })}
            >
              {m.level}
            </li>
          ))}
        </ol>
      </div>
      <BuyButton buy={r.buy} req={r.req} testId={`stars-buy-${r.id}`} t={t} onPurchase={onPurchase} />
    </li>
  );
}

function TechCard({ r, t, onPurchase }: { r: TechRow; t: TabsT; onPurchase(req: PurchaseRequest): void }) {
  return (
    <li class="tabs-row" data-testid={`tech-row-${r.id}`}>
      <span class="tabs-ico">
        <Icon name="gear" size={36} />
      </span>
      <div class="tabs-main">
        <div class="tabs-title">
          {t(r.name)}
          <span class="tabs-lvl" data-testid={`tech-lv-${r.id}`} data-level={r.level}>
            {t("tabs.techLevel", { level: r.level, max: r.maxLevel })}
          </span>
        </div>
        <div class="tabs-sub">{t(r.description)}</div>
      </div>
      <BuyButton buy={r.buy} req={r.req} testId={`tech-buy-${r.id}`} t={t} onPurchase={onPurchase} />
    </li>
  );
}

export function TabsView({ model, tab: routeTab, actions, t, mode = "sheet", hint = null }: TabsViewProps) {
  const panel = mode === "panel";
  const tab: TabId | null = panel ? (routeTab ?? "heroes") : routeTab;
  const beta = getBetaTabs();
  const dot = (id: TabId) => id !== "heroes" && model.affordable[id];
  const bar = (
    <nav class="tabs-bar" aria-label={t("tabs.bar")}>
      {TABS.map((id) => (
        <button
          key={id}
          type="button"
          class={`tabs-tab${tab === id ? " tabs-tab--on" : ""}${id === "level" && hint?.at === "tab" ? " hint-target" : ""}`}
          aria-pressed={tab === id}
          data-testid={`tab-${id}`}
          onClick={() => actions.onSelect(id)}
        >
          <Icon name={TAB_ICON[id]} size={28} />
          <span>{t(`tabs.${id}`)}</span>
          {dot(id) && <span class="kit-badge" aria-hidden="true" />}
          {id === "level" && hint?.at === "tab" && <HintMark label={t("tabs.hintLevel")} testId="hint-level" />}
        </button>
      ))}
    </nav>
  );
  return (
    <div class={`tabs kit${panel ? " tabs--panel" : ""}`} data-testid="tabs" data-tab={tab ?? ""} data-mode={mode}>
      {panel && bar}
      {tab && (
        <section class="tabs-sheet" data-testid={`tabs-sheet-${tab}`} aria-label={t(`tabs.${tab}`)}>
          <header class="tabs-head">
            <h2>{t(`tabs.${tab}`)}</h2>
            <Plate class="tabs-coins" data-testid="tabs-coins" data-value={model.coins}>
              <Icon name="coin" size={22} />
              {formatCompact(model.coins)}
              {model.incoming > 0 && (
                <small class="tabs-coins__incoming" data-testid="tabs-coins-incoming" data-value={model.incoming}>
                  {t("tabs.coinsIncoming", { n: formatCompact(model.incoming) })}
                </small>
              )}
            </Plate>
            {!panel && (
              <button
                type="button"
                class="tabs-x"
                aria-label={t("tabs.close")}
                data-testid="tabs-close"
                onClick={actions.onClose}
              >
                ×
              </button>
            )}
          </header>
          <ul class="tabs-list">
            {tab === "heroes" &&
              (beta ? <beta.heroes /> : model.heroes.map((r) => <HeroCard key={r.id} r={r} t={t} />))}
            {tab === "level" &&
              model.level.map((r) => (
                <LevelCard
                  key={r.id}
                  r={r}
                  t={t}
                  hinted={hint?.at === "buy" && hint.heroId === r.id}
                  onPurchase={actions.onPurchase}
                />
              ))}
            {tab === "stars" &&
              (beta ? (
                <beta.stars />
              ) : (
                model.stars.map((r) => <StarCard key={r.id} r={r} t={t} onPurchase={actions.onPurchase} />)
              ))}
            {tab === "tech" && beta && <beta.techHead />}
            {tab === "tech" &&
              model.tech
                .filter((r) => !beta || r.id !== "tech_base")
                .map((r) => <TechCard key={r.id} r={r} t={t} onPurchase={actions.onPurchase} />)}
          </ul>
        </section>
      )}
      {!panel && bar}
    </div>
  );
}
